import React from 'react'
import { gql, useQuery } from '@apollo/client'

export type ApiMonitoringInterval = 'DAY' | 'WEEK' | 'MONTH'

export type OperationLogSource = 'query' | 'mutation' | 'field' | 'request' | 'route' | 'job' | 'internal'

export type ApiMonitoring = {
	from: string
	to: string
	totalCount: number
	errorCount: number
	bugCount: number
	rateLimitedCount: number
	userCount: number
	ipCount: number
	emailCount: number
	newRegisteredUserCount: number
	newAnonymousUserCount: number
	bonusGrantedInCents: number
	// Ein Zeitabschnitt je Balken im Verlauf, z. B. eine Stunde in der Tagesansicht.
	timeline: { startsAt: string; count: number; errorCount: number; rateLimitedCount: number }[]
	operations: {
		operationName: string
		source: OperationLogSource
		count: number
		errorCount: number
		rateLimitedCount: number
		userCount: number
		p95DurationMs: number | null
		lastCalledAt: string
	}[]
	// Nur Fehler und Rate-Limit-Sperren, die neuesten zuerst.
	entries: {
		datetime: string
		source: OperationLogSource
		operationName: string
		userId: string | null
		isAnonymous: boolean
		isRoot: boolean
		ip: string | null
		params: string
		status: 'ERROR' | 'RATE_LIMITED'
		errorCode: string | null
		errorMessage: string | null
		errorStack: string | null
		durationMs: number | null
	}[]
}

const API_MONITORING_QUERY = gql`
	query ApiMonitoring($intervalType: ApiMonitoringInterval!, $offset: Int!, $operationName: String) {
		apiMonitoring(intervalType: $intervalType, offset: $offset, operationName: $operationName) {
			from
			to
			totalCount
			errorCount
			bugCount
			rateLimitedCount
			userCount
			ipCount
			emailCount
			newRegisteredUserCount
			newAnonymousUserCount
			bonusGrantedInCents
			timeline {
				startsAt
				count
				errorCount
				rateLimitedCount
			}
			operations {
				operationName
				source
				count
				errorCount
				rateLimitedCount
				userCount
				p95DurationMs
				lastCalledAt
			}
			entries {
				datetime
				source
				operationName
				userId
				isAnonymous
				isRoot
				ip
				params
				status
				errorCode
				errorMessage
				errorStack
				durationMs
			}
		}
	}
`

// Geloggte API-Aufrufe und Fehler aus operation_logs für ein Zeitfenster; offset 0 ist das laufende.
export const useApiMonitoring = (
	intervalType: ApiMonitoringInterval,
	offset: number,
	operationName: string | null
): { monitoring?: ApiMonitoring; loading: boolean; refetch: () => void } => {
	const { data, refetch } = useQuery(API_MONITORING_QUERY, {
		variables: { intervalType, offset, operationName },
		fetchPolicy: 'cache-and-network',
	})

	return React.useMemo(
		() => ({
			monitoring: data?.apiMonitoring,
			loading: data?.apiMonitoring === undefined,
			refetch,
		}),
		[data?.apiMonitoring, refetch]
	)
}
